import { MALAYSIA_STATES } from './malaysia';
import { freshFiltersForMarket, marketFor } from './markets';
import { SQ_METRES_PER_ACRE } from './units';
import type { Filters, ParsedQuery, PlantRequest } from './types';

const PLANT_ALIASES: Record<string, string[]> = {
  roses: ['roses', 'rose', 'mawar'], orchids: ['orchids', 'orchid', 'orkid'],
  hibiscus: ['hibiscus', 'bunga raya'], chrysanthemums: ['chrysanthemums', 'chrysanthemum', 'kekwa'],
  peonies: ['peonies', 'peony'], lavender: ['lavender'], hydrangea: ['hydrangea', 'hydrangeas'],
  'english lavender': ['english lavender'], 'french lavender': ['french lavender'],
  'panicle hydrangea': ['panicle hydrangea'], 'bigleaf hydrangea': ['bigleaf hydrangea'],
  dahlias: ['dahlias', 'dahlia'], sunflowers: ['sunflowers', 'sunflower'],
  jasmine: ['jasmine', 'melur'], bougainvillea: ['bougainvillea', 'bunga kertas'],
  cosmos: ['cosmos'], zinnias: ['zinnias', 'zinnia'], apples: ['apples', 'apple'],
  mokara: ['mokara'], dendrobium: ['dendrobium'],
};
const WORD_PLANTS = Object.entries(PLANT_ALIASES).flatMap(([key, aliases]) => aliases.map(alias => ({ key, alias }))).sort((a, b) => b.alias.length - a.alias.length);
const escapeRE = (text: string) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const wordPattern = (text: string) => new RegExp(`\\b${escapeRE(text)}\\b`, 'gi');

export function freshFilters(base: Partial<Filters> = {}): Filters {
  return freshFiltersForMarket(base.country ?? 'US', base);
}

/** Bounded, deterministic parsing. Quoted cultivar spelling never undergoes normalization. */
export function parseQuery(query: string, base: Partial<Filters> = {}): ParsedQuery {
  const filters = freshFilters(base);
  const text = query.slice(0, 2000);
  let remaining = text;
  const result: ParsedQuery = { filters, clarifications: [], unparsed: [] };
  // Dollar signs use the selected US/Canada context; explicit currencies never imply an exchange rate.
  const amounts = [...text.matchAll(/(?<![\w$])((?:USD|CAD|MYR|RM)|(?:US|CA|C)?\$)\s*([\d,]+(?:\.\d+)?)\s*(million|ribu|juta|[km])?\b(?:\s*(USD|CAD|MYR)\b)?(\s*(?:per|\/|se)\s*(?:acre|ekar)\b)?/gi)];
  const currencyOf = (symbol: string): string | null => /^(?:RM|MYR)$/i.test(symbol) ? 'MYR'
    : /^(?:CAD|CA\$|C\$)$/i.test(symbol) ? 'CAD' : /^(?:USD|US\$)$/i.test(symbol) ? 'USD'
      : filters.country === 'US' ? 'USD' : filters.country === 'CA' ? 'CAD' : null;
  const currencies = new Set(amounts.flatMap(money => [currencyOf(money[4] ?? money[1]!), ...(money[4] && money[1] !== '$' ? [currencyOf(money[1]!)] : [])]).filter((currency): currency is string => currency !== null));
  if (currencies.size > 1) result.unparsed.push('Choose one budget currency; USD, CAD and MYR amounts are not converted or combined.');
  let totalFound = false;
  for (const money of amounts) {
    const currency = currencyOf(money[4] ?? money[1]!);
    if (!currency) { result.unparsed.push('Choose the currency for the $ budget; Malaysia prices use RM.'); continue; }
    if (currencies.size > 1) continue;
    const multiplier = /^(?:m|million|juta)$/i.test(money[3] ?? '') ? 1_000_000 : /^(?:k|ribu)$/i.test(money[3] ?? '') ? 1_000 : 1;
    const value = Number(money[2]!.replaceAll(',', '')) * multiplier;
    if (!Number.isFinite(value) || value > 1e12) { result.unparsed.push('The budget amount is outside the supported range.'); continue; }
    if (filters.currency !== currency) { filters.maxPrice = null; filters.maxPricePerAcre = null; }
    filters.currency = currency;
    if (money[5]) filters.maxPricePerAcre = value;
    else if (!totalFound) { filters.maxPrice = value; totalFound = true; }
  }
  const area = /(?:at least|min(?:imum)?|over|more than)?\s*([\d,]+(?:\.\d+)?)\s*(acres?|ekar|hectares?|hektar|ha|sq\.?\s*m|sqm|m²|square met(?:re|er)s?|sq\.?\s*ft|sqft|square feet)(?![a-z0-9])/i.exec(text);
  if (area) {
    const amount = Number(area[1]!.replaceAll(',', ''));
    const unit = area[2]!.toLowerCase();
    filters.minAcres = /^(?:ac|ekar)/.test(unit) ? amount : /^(?:h)/.test(unit) ? amount * 10_000 / SQ_METRES_PER_ACRE : /(?:ft|feet)/.test(unit) ? amount / 43_560 : amount / SQ_METRES_PER_ACRE;
  }
  if (/\b(?:needs? work|fixer|renovat|boleh dibaiki)/i.test(text)) filters.home = 'work';
  else if (/\b(?:any condition|vacant land|land only|tanah kosong)\b/i.test(text)) filters.home = 'any';
  else if (/\b(?:livable|liveable|move.in|ready to live|turnkey|existing (?:home|house))\b/i.test(text)) filters.home = 'ready';
  // Mentioning a house on farmland means a house would be good to have, not that bare land is ruled out.
  else if (/\b(?:home|house|bungalow|banglo|rumah|cabin|chalet)\b/i.test(text)) filters.home = 'nice';
  if (filters.country === 'MY') {
    if (/\b(?:freehold|hak milik kekal)\b/i.test(text)) filters.ownership = 'freehold';
    if (/\b(?:leasehold is fine|leasehold okay|any tenure)\b/i.test(text)) filters.ownership = 'any';
  }
  if (/\b(?:room to walk|walk on (?:the )?(?:land|property)|walking space|space to walk)\b/i.test(text)) filters.walking = 'onsite';
  else if (/\b(?:nearby walks?|nearby trails?|walk nearby)\b/i.test(text)) filters.walking = 'nearby';
  if (/\b(?:tested (?:internet|connection)|speed.?tested|internet tested)\b/i.test(text)) filters.internet = 'tested';
  else if (/\b(?:internet|fibre|fiber|broadband|remote work|work from home|wifi)\b/i.test(text)) filters.internet = 'mapped';
  const noise = new Set(filters.noise);
  if (/\b(?:quiet|peaceful|low noise|tenang)\b/i.test(text)) for (const type of ['road', 'rail', 'aircraft', 'industrial', 'recreational', 'animal'] as const) noise.add(type);
  for (const [type, pattern] of [
    ['road', /(?:no|away from|avoid|without|low)\s+(?:busy )?(?:roads?|traffic|highway)/i],
    ['rail', /(?:no|away from|avoid|without)\s+(?:trains?|rail)/i],
    ['aircraft', /(?:no|away from|avoid|without)\s+(?:aircraft|airports?|planes?)/i],
    ['industrial', /(?:no|away from|avoid|without)\s+(?:industr(?:y|ial)|factories|quarr)/i],
    ['recreational', /(?:no|away from|avoid|without)\s+(?:recreational noise|motorbikes?)/i],
    ['animal', /(?:no|away from|avoid|without)\s+(?:farm animals?|animal noise)/i],
  ] as const) if (pattern.test(text)) noise.add(type);
  filters.noise = [...noise];

  // Associate a quoted cultivar with the nearest preceding recognized plant.
  const plants: PlantRequest[] = [];
  const quotes = [...text.matchAll(/"([^"]{1,100})"|“([^”]{1,100})”|(?<!\w)'([^']{1,100})'|‘([^’]{1,100})’/g)];
  for (const quote of quotes) {
    const before = text.slice(Math.max(0, quote.index! - 50), quote.index);
    const matching = WORD_PLANTS.find(({ alias }) => new RegExp(`\\b${escapeRE(alias)}\\s*$`, 'i').test(before));
    const cultivar = (quote[1] ?? quote[2] ?? quote[3] ?? quote[4])!;
    const key = matching?.key ?? 'unidentified';
    plants.push({ key, label: matching ? `${matching.key} '${cultivar}'` : `'${cultivar}'`, cultivar });
    remaining = remaining.replace(quote[0], ' '.repeat(quote[0].length));
    if (matching) remaining = remaining.replace(new RegExp(`\\b${escapeRE(matching.alias)}(?=\\s{${quote[0].length},})`, 'i'), '');
  }
  for (const { key, alias } of WORD_PLANTS) {
    if (!wordPattern(alias).test(remaining)) continue;
    remaining = remaining.replace(wordPattern(alias), ' ');
    if (!plants.some(plant => plant.key === key)) plants.push({ key, label: key });
  }
  // Do not discard existing chips when parsing an additional query.
  filters.plants = [...filters.plants, ...plants].filter((plant, index, all) => all.findIndex(other => other.key === plant.key && other.cultivar === plant.cultivar) === index);
  for (const plant of filters.plants) {
    if (plant.cultivar) continue;
    if (plant.key === 'lavender') result.clarifications.push({ key: plant.key, question: 'Which lavender do you mean?', options: [{ key: 'english lavender', label: 'English lavender' }, { key: 'french lavender', label: 'French lavender' }] });
    if (plant.key === 'hydrangea') result.clarifications.push({ key: plant.key, question: 'Which hydrangea do you mean?', options: [{ key: 'panicle hydrangea', label: 'Panicle hydrangea' }, { key: 'bigleaf hydrangea', label: 'Bigleaf hydrangea' }] });
  }
  const places: [string, string][] = filters.country === 'MY' ? [
    ...MALAYSIA_STATES.map(state => [state.replace('W.P. ', ''), state] as [string, string]),
    ['KL', 'Kuala Lumpur'], ['PJ', 'Petaling Jaya'], ['Penang', 'Pulau Pinang'],
    ...['Janda Baik', 'Bentong', 'Semenyih', 'Hulu Langat', 'Kuala Kubu Bharu', 'Seremban', 'Ipoh', 'Kuching', 'Kota Kinabalu', 'Cameron Highlands', 'Johor Bahru', 'Kundasang', 'Taiping'].map(name => [name, name] as [string, string]),
  ] : [];
  const location = places.sort((a, b) => b[0].length - a[0].length).find(([alias]) => wordPattern(alias).test(text));
  if (location) filters.location = location[1];
  if (filters.country === 'US' || filters.country === 'CA') {
    const county = /\b(?:in|within|near)\s+([A-Za-z][A-Za-z.'-]*(?:\s+[A-Za-z][A-Za-z.'-]*){0,3})\s+County\b/i.exec(text)
      ?? /\b([A-Z][a-zA-Z.'-]*(?:\s+[A-Z][a-zA-Z.'-]*){0,3})\s+County\b/.exec(text)
      ?? /^([A-Za-z][A-Za-z.'-]*(?:\s+[A-Za-z][A-Za-z.'-]*){0,3})\s+County\b/i.exec(text);
    if (county) filters.county = county[1]!.trim();
    // Washington County in Idaho must not select Washington state.
    const regionText = county ? text.replace(county[0], '') : text;
    const regions = marketFor(filters.country).states;
    const state = [...regions].sort((a, b) => b.name.length - a.name.length).find(region => wordPattern(region.name).test(regionText))
      // Upper-case abbreviations are intentional; normal words like "in" and "or" are not states.
      ?? regions.find(region => new RegExp(`\\b${region.code}\\b`).test(regionText))
      ?? regions.find(region => new RegExp(`,\\s*${region.code}\\b`, 'i').test(regionText));
    if (state) filters.stateCode = state.code;
  }
  return result;
}
